import { useEffect, useState } from "react";

import { FrownOutlined } from "@ant-design/icons";
import { Skeleton, Select, Button, Row, Col, Modal } from "antd";
import { t } from "i18next";

import usePropertiesWithSystems from "../../../../../api/hooks/usePropertiesWithSystems";
import useZonesByPropertyId from "../../../../../api/hooks/useZoneByPropertyId";
import useWindowSize from "../../../../../hooks/useWindowSize";
import UnsavedChangesWarning from "../../../../Shared/UnsavedChangesWarning";
import ZoneCard from "./ZoneCard";
import ZoneCardColumns from "./ZoneCardColumns";
import ZoneCreate from "./ZoneCreate";

function ZoneManagementPropertyList() {
  const window = useWindowSize();
  const pageSize = 10;

  const [selectedPropertyId, setSelectedPropertyId] = useState("");
  const [isNewZoneModalOpen, setIsNewZoneModalOpen] = useState(false);
  const [currentZone, setCurrentZone] = useState(null);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [editFormFloorFormReference, setEditFloorFormReference] = useState();
  const [isAlertUnsavedChangesOpen, setAlertUnsavedChangesOpen] =
    useState(false);

  const { data: properties } = usePropertiesWithSystems();

  const {
    data: zones,
    status: metadataQueryStatus,
    refetch: refetchZones,
  } = useZonesByPropertyId(selectedPropertyId, !!selectedPropertyId);

  const onChangeProperty = (value) => {
    if (!value) {
      setSelectedPropertyId("");
    } else {
      setSelectedPropertyId(value);
    }
  };

  const propertyOptions = [
    {
      value: "",
      label: t("statistics_filters.select_property"),
    },
  ];

  if (properties && properties) {
    properties.forEach((p) =>
      propertyOptions.push({ value: p.Id, label: p.Name }),
    );
  }

  const filterSelect = (input, option) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  useEffect(() => {}, [
    selectedPropertyId,
    properties,
    isFormDirty,
    editFormFloorFormReference,
    refetchZones,
  ]);

  const onCancelButtonClick = () => {
    if (isFormDirty) {
      setAlertUnsavedChangesOpen(true);
    } else {
      setCurrentZone(null);
      setIsNewZoneModalOpen(false);
      editFormFloorFormReference.resetFields();
      refetchZones();
    }
  };

  return (
    <div className="zone-management-property-list content-wrapper overflow-y-auto">
      <div className="flex md:items-center mb-2 flex-col-reverse md:flex-row">
        <div className="flex-1 mt-6 md:mt-1 flex lg:items-center flex-col lg:flex-row">
          {window?.width > 768 && (
            <h1 className="page-title mb-0 !text-triple-blue">
              {t("zone_management_property_list.zones")}
            </h1>
          )}
          {window?.width <= 768 && (
            <h1 className="page-title-mobile mb-0 text-center sm:text-left uppercase !text-triple-blue">
              {t("zone_management_property_list.zones")}
            </h1>
          )}
        </div>
      </div>

      <Row className="justify-between my-6">
        <Col>
          <Select
            size="small"
            showSearch
            placeholder={t("statistics_filters.all_properties")}
            optionFilterProp="property"
            onChange={onChangeProperty}
            filterOption={filterSelect}
            className="mb-1 xl:mb-2 xl:w-64"
            defaultValue=""
            options={propertyOptions}
          />
        </Col>
        <Col>
          <Button
            className="py-2 px-4 rounded-full"
            type="primary"
            onClick={() => {
              setCurrentZone(null);
              setIsNewZoneModalOpen(true);
            }}
          >
            {t("zone_management_property_list.new_zone")}
          </Button>
        </Col>
      </Row>

      <Modal
        className="property-management-floor-edit-modal"
        maskClosable={false}
        footer={null}
        open={isNewZoneModalOpen}
        keyboard={false}
        onCancel={() => onCancelButtonClick()}
        destroyOnClose
        width={1800}
        bodyStyle={{ height: "75vh", overflowY: "auto", marginTop: "1.5rem" }}
      >
        <ZoneCreate
          propertyId={Number(selectedPropertyId)}
          setIsFormDirty={setIsFormDirty}
          setEditFloorFormReference={setEditFloorFormReference}
          currentZone={currentZone}
          setIsNewZoneModalOpen={setIsNewZoneModalOpen}
          setCurrentZone={setCurrentZone}
        />
      </Modal>
      <UnsavedChangesWarning
        onSaveClick={() => {
          setAlertUnsavedChangesOpen(false);
          editFormFloorFormReference.submit();
        }}
        onDontSaveClick={() => {
          setAlertUnsavedChangesOpen(false);
          setIsNewZoneModalOpen(false);
          setIsFormDirty(false);
          editFormFloorFormReference.resetFields();
          refetchZones();
        }}
        onCancelClick={() => {
          setAlertUnsavedChangesOpen(false);
        }}
        isAlertUnsavedChangesOpen={isAlertUnsavedChangesOpen}
      />

      {!selectedPropertyId && (
        <div className="p-6 text-white text-opacity-80 text-center">
          {t("zone_management_property_list.select_property")}
        </div>
      )}

      {metadataQueryStatus === "loading" &&
        !!selectedPropertyId &&
        Array.from({ length: pageSize }, (value, index) => index).map((el) => (
          <Skeleton
            active
            paragraph={{
              rows: 3,
            }}
            key={el}
          />
        ))}

      {metadataQueryStatus === "success" && zones?.length === 0 && (
        <div className="p-6 text-white text-opacity-80">
          <FrownOutlined className="text-xl mr-2" />
          {t("zone_management_property_list.no_matches")}
        </div>
      )}

      {metadataQueryStatus === "success" && zones?.length > 0 && (
        <div className="sm:grid sm:grid-cols-2 lg:grid-cols-1 gap-x-6 lg:gap-x-0">
          <ZoneCardColumns />
          {selectedPropertyId &&
            zones.map((zone, index) => (
              <ZoneCard
                // eslint-disable-next-line react/no-array-index-key
                key={`${zone.ZoneUniqueId}_${index}`}
                zone={zone}
                setIsNewZoneModalOpen={setIsNewZoneModalOpen}
                setCurrentZone={setCurrentZone}
                refetchZones={refetchZones}
              />
            ))}
        </div>
      )}
    </div>
  );
}

export default ZoneManagementPropertyList;
